import type { RefObject } from 'react';
import { useEffect } from 'react';

/*
 * The hero's live ping instrument (design app.js §4 `initHud`). ONE figure drives the big reading, the
 * "Com NoPing" bar and the percentage, so no two numbers on the card can disagree, and the percentage is
 * computed from the figures on screen. The entrance plays once: the reading starts at the provider's 208 ms
 * and falls to 2–5 ms, then keeps reading live (a new whole number in 2–5 every 0.8–1.5 s, tweened). The
 * "Sem NoPing" row wanders 196–224 ms. The trace scrolls in real time. Off screen or in a background tab
 * nothing runs; coming back it keeps reading live (no second 208). Under reduced motion the settled frame
 * shipped in the markup is left as is. React renders that settled frame; this only mutates text, `d` and
 * CSS variables of nodes React never re-renders with different props.
 */

const BASE = 208;
const SCALE = 224;
const MIN_W = 3;
const LO = 2;
const HI = 5;
const FALL = 1700;
const STEP = 130;
const SAMPLES = 24;
const WIDTH = 200;
const HEIGHT = 64;
const DX = WIDTH / (SAMPLES - 1);

type Ease = (progress: number) => number;
const easeOut: Ease = (progress) => 1 - (1 - progress) ** 3;
const easeInOut: Ease = (progress) => (progress < 0.5 ? 2 * progress * progress : 1 - (2 - 2 * progress) ** 2 / 2);

class Tween {
  from: number;

  to: number;

  value: number;

  startedAt = 0;

  duration = 1;

  ease: Ease = easeInOut;

  constructor(value: number) {
    this.from = value;
    this.to = value;
    this.value = value;
  }

  go(to: number, now: number, duration: number, ease: Ease = easeInOut) {
    this.from = this.value;
    this.to = to;
    this.startedAt = now;
    this.duration = duration;
    this.ease = ease;
  }

  at(now: number) {
    const progress = Math.min(1, Math.max(0, (now - this.startedAt) / this.duration));
    this.value = this.from + (this.to - this.from) * this.ease(progress);
  }
}

export function useLivePingHud(hudRef: RefObject<HTMLElement>): void {
  useEffect(() => {
    const hud = hudRef.current;
    if (!hud || typeof window.requestAnimationFrame !== 'function') return undefined;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const numEl = hud.querySelector<HTMLElement>('[data-ping-value]');
    const pctEl = hud.querySelector<HTMLElement>('[data-ping-pct]');
    const goodNumEl = hud.querySelector<HTMLElement>('[data-ping-bar]');
    const goodBarEl = hud.querySelector<HTMLElement>('[data-ping-good-bar]');
    const badNumEl = hud.querySelector<HTMLElement>('[data-ping-bad]');
    const badBarEl = hud.querySelector<HTMLElement>('[data-ping-bad-bar]');
    const line = hud.querySelector<SVGPathElement>('.v1-hud__line');
    const area = hud.querySelector<SVGPathElement>('.v1-hud__area');
    const dot = hud.querySelector<HTMLElement>('.v1-hud__dot');
    if (!numEl || !line || !area) return undefined;

    let seed = 88171;
    const random = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    const between = (low: number, high: number) => low + random() * (high - low);
    const pickInt = (low: number, high: number, not?: number) => {
      let value: number;
      do value = low + Math.floor(random() * (high - low + 1));
      while (value === not);
      return value;
    };
    const widthOf = (ms: number) => MIN_W + (Math.min(ms, SCALE) / SCALE) * (100 - MIN_W);
    const yOf = (ms: number) => 56 - (Math.min(ms, SCALE) / SCALE) * 46;
    const badTarget = () => {
      const value = random() < 0.18 ? between(216, 224) : 208 + (random() - 0.5) * 24;
      return Math.max(196, Math.min(224, Math.round(value)));
    };

    const good = new Tween(BASE);
    const bad = new Tween(BASE);
    const points: number[] = Array.from({ length: SAMPLES }, () => BASE);
    let fraction = 0;
    let pending = true;
    let falling = false;
    let onScreen = false;
    let raf = 0;
    let last = 0;
    let nextGood = 0;
    let nextBad = 0;
    const shown: Record<string, string> = {};

    const put = (key: string, element: HTMLElement | null, text: string) => {
      if (element && shown[key] !== text) {
        element.replaceChildren(text);
        shown[key] = text;
      }
    };

    const paint = () => {
      let path = '';
      points.forEach((point, index) => {
        path += `${index ? ' L' : 'M'}${((index - fraction) * DX).toFixed(1)} ${yOf(point).toFixed(1)}`;
      });
      path += ` L${WIDTH} ${yOf(good.value).toFixed(1)}`;
      line.setAttribute('d', path);
      area.setAttribute('d', `${path} L${WIDTH} ${HEIGHT} L${(-fraction * DX).toFixed(1)} ${HEIGHT} Z`);
      dot?.style.setProperty('--dot-y', `${((yOf(good.value) / HEIGHT) * 100).toFixed(2)}%`);
      const ms = Math.round(good.value);
      const pct = Math.round(((BASE - ms) / BASE) * 100);
      put('num', numEl, String(ms));
      put('bar', goodNumEl, String(ms));
      put('pct', pctEl, pct > 0 ? `−${pct}%` : '0%');
      put('bad', badNumEl, String(Math.round(bad.value)));
      goodBarEl?.style.setProperty('--w', `${widthOf(good.value).toFixed(2)}%`);
      badBarEl?.style.setProperty('--w', `${widthOf(bad.value).toFixed(2)}%`);
    };

    const frame = (now: number) => {
      raf = window.requestAnimationFrame(frame);
      const elapsed = Math.min(250, Math.max(0, now - last));
      last = now;
      if (now >= nextGood) {
        good.at(now);
        good.go(pickInt(LO, HI, Math.round(good.to)), now, 450);
        nextGood = now + between(800, 1500);
      }
      if (now >= nextBad) {
        bad.at(now);
        bad.go(badTarget(), now, 240);
        nextBad = now + between(350, 750);
      }
      good.at(now);
      bad.at(now);
      if (falling && now >= good.startedAt + FALL) {
        falling = false;
        hud.classList.remove('is-falling');
      }
      fraction += elapsed / STEP;
      while (fraction >= 1) {
        points.shift();
        points.push(good.value);
        fraction -= 1;
      }
      paint();
    };

    const start = () => {
      if (raf) return;
      const now = window.performance.now();
      if (pending) {
        pending = false;
        falling = true;
        hud.classList.add('is-falling');
        good.go(pickInt(LO, HI), now, FALL, easeOut);
        nextGood = now + FALL + between(800, 1500);
      }
      nextBad = Math.max(nextBad, now + between(350, 750));
      last = now;
      raf = window.requestAnimationFrame(frame);
    };
    const stop = () => {
      if (!raf) return;
      window.cancelAnimationFrame(raf);
      raf = 0;
    };

    // the entrance frame: 208 ms everywhere, painted while the reveal still hides the card
    hud.classList.remove('is-falling');
    paint();

    const onVisibility = () => {
      if (document.hidden) stop();
      else if (onScreen) start();
    };

    let observer: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            onScreen = entry.isIntersecting;
            if (!onScreen) stop();
            else if (entry.intersectionRatio >= 0.3 && !document.hidden) start();
          });
        },
        { threshold: [0, 0.3] }
      );
      observer.observe(hud);
    } else {
      onScreen = true;
      start();
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      hud.classList.remove('is-falling');
    };
  }, [hudRef]);
}
